import { SortType } from '../../../../../components/ColumnHeader';
import { getPickleBallRating, getRatingDetails, setRatingChoice } from '../../../../../utils';
import { ComparatorTypes } from '../../../../../utils/comparator';
import { gameTypes, sortParamTypes } from '../../../../../utils/constants';
import { allDivisions, divisionFormatTypeIds, freeDivision, paymentProviderTypeIds, registrationGroupStatusTypeIds, registrationStateTypeIds, registrationStatusTypeIds, sportTypeIds } from '../../../../../utils/eventConstants';
import { mergeArraysOfObjectsById } from '../../../../../utils/misc';
import { shouldDisplayAllDivisionsFilterInPlayerList } from '../../../../../utils/events';

export const getSortValue = (player, sortPrm, utrTypeId, userIsPowered, showDecimals) => {
    switch (sortPrm) {
        case sortParamTypes.SINGLESUTR: {
            const playerSinglesRating = getRatingDetails(setRatingChoice({ player, ratingType: 'singles', utrType: utrTypeId }), userIsPowered, showDecimals);
            return playerSinglesRating.sortRating;
        }
        case sortParamTypes.DOUBLESUTR: {
            const playerDoublesRating = getRatingDetails(setRatingChoice({ player, ratingType: 'doubles', utrType: utrTypeId }), userIsPowered, showDecimals);
            return playerDoublesRating.sortRating;
        }
        case sortParamTypes.SINGLESPKB: {
            return getPickleBallRating(player, gameTypes.SINGLES, true)?.sortRating || 0;
        }
        case sortParamTypes.DOUBLESPKB: {
            return getPickleBallRating(player, gameTypes.DOUBLES, true)?.sortRating || 0;
        }
        case sortParamTypes.UTRP: {
            const pickleballRating = getPickleBallRating(player, null, true);
            return pickleballRating.sortRating;
        }
        default:
            return player[sortPrm];
    }
};

export const getComparatorType = (srtParam) => {
    switch (srtParam) {
        case sortParamTypes.SINGLESUTR:
        case sortParamTypes.DOUBLESUTR:
        case sortParamTypes.UTAORDER:
        case sortParamTypes.UTRP:
        case sortParamTypes.SINGLESPKB:
        case sortParamTypes.DOUBLESPKB:
        case sortParamTypes.AGE:
            return ComparatorTypes.NUMBER;
        default:
            return ComparatorTypes.STRING;
    }
};

export const setPlayerListPrimarySortValue = (list, sortParam, utrTypeId, userIsPowered, showDecimals) => list.map((player) => {
    switch (sortParam) {
        case sortParamTypes.NAME:
            return {
                ...player,
                primarySortValue: player.lastName,
                secondarySortValue: player.firstName,
            };
        case sortParamTypes.SINGLESUTR:
        case sortParamTypes.DOUBLESUTR:
        case sortParamTypes.SINGLESPKB:
        case sortParamTypes.DOUBLESPKB:
        case sortParamTypes.AGE:
        case sortParamTypes.UTRP:
            return {
                ...player,
                primarySortValue: parseFloat(getSortValue(player, sortParam, utrTypeId, userIsPowered, showDecimals)),
            };
        case sortParamTypes.REGDATE:
            return {
                ...player,
                primarySortValue: getSortValue(player, sortParam, utrTypeId, userIsPowered, showDecimals),
            };
        default:
            return { ...player, primarySortValue: '' };
    }
});

export const setTeamListPrimarySortValue = (list, sortParam, utrTypeId, userIsPowered, showDecimals) => list.map((team) => {
    const player1 = team.players[0];
    const doubles = team.players.length > 1;
    const player2 = doubles ? team.players[1] : {};

    switch (sortParam) {
        case sortParamTypes.NAME:
            return {
                ...team,
                primarySortValue: doubles ? `${player1.lastName} ${player2.lastName}` : player1.lastName,
                secondarySortValue: doubles ? `${player1.firstName} ${player2.firstName}` : player1.firstName,
            };
        case sortParamTypes.SINGLESUTR:
        case sortParamTypes.DOUBLESUTR:
        case sortParamTypes.SINGLESPKB:
        case sortParamTypes.DOUBLESPKB:
        case sortParamTypes.AGE:
        case sortParamTypes.UTRP: {
            const player1Rating = parseFloat(getSortValue(player1, sortParam, utrTypeId, userIsPowered, showDecimals));
            const player2Rating = parseFloat(getSortValue(player2, sortParam, utrTypeId, userIsPowered, showDecimals));
            return {
                ...team,
                primarySortValue: doubles ? (player1Rating + player2Rating) / 2 : player1Rating,
            };
        }
        default:
            return { ...team, primarySortValue: '' };
    }
});

export const getComparatorArguments = (srtParam, srtType) => {
    const args = [];
    const primarySortArg = {
        type: getComparatorType(srtParam),
        field: 'primarySortValue',
        descend: srtType === SortType.DESC,
    };
    args.push(primarySortArg);
    if (['name', 'utrOrder'].includes(srtParam)) {
        const secondarySortArg = {
            type: getComparatorType(srtParam),
            field: 'secondarySortValue',
            descend: srtType === SortType.DESC,
        };
        args.push(secondarySortArg);
    }
    return args;
};

export const shouldUseStripeWithdrawFlow = (eventData, currentDivision, divisionName) => {
    return eventData?.paymentProviderTypeId === paymentProviderTypeIds.STRIPE ||
        currentDivision?.price === freeDivision ||
        divisionName === allDivisions ||
        !!eventData?.allowOffPlatformPayments;
};

export const shouldUseStripeRefundFlow = (eventData) => {
    return eventData?.paymentProviderTypeId === paymentProviderTypeIds.STRIPE;
};

export const getWithdrawRefundConfig = (eventData, currentDivision, playerToWithdraw) => {
    let showRefundOption = true;
    let initialWithdrawRefund = eventData.registrationStateId === registrationStateTypeIds.OPEN_REGISTRATION &&
        !!playerToWithdraw?.refundable;
    if (currentDivision?.price === freeDivision || eventData.allowOffPlatformPayments) {
        showRefundOption = false;
        initialWithdrawRefund = !eventData.allowOffPlatformPayments;
    }
    return { showRefundOption, initialWithdrawRefund };
};

export const shouldShowCustomSplitMessage = (matchingDivisions, isAdmin, isPaySep) => {
    return (
        !!isAdmin &&
        (matchingDivisions[0]?.registrationStatusTypeId === registrationStatusTypeIds.REGISTERED ||
            matchingDivisions[0]?.registrationStatusTypeId === registrationStatusTypeIds.CREATED) &&
        matchingDivisions[0]?.registrationGroupStatus === registrationGroupStatusTypeIds.REGISTERED &&
        !!isPaySep
    );
};

export const mergePlayerWithWithdrawn = (playersData, withdrawnPlayers, playerId) => {
    const dataPlayer = playersData?.find(player => player.playerId === playerId);
    const withdrawnPlayer = withdrawnPlayers?.find(player => player.playerId === playerId);
    const player = dataPlayer || withdrawnPlayer;
    if (dataPlayer && withdrawnPlayer) {
        return {
            ...dataPlayer,
            divisions: mergeArraysOfObjectsById(dataPlayer.divisions, withdrawnPlayer.divisions),
        };
    }
    return player;
};

export const buildInitialPlayersTabState = ({
    eventDivisions,
    selectedDivisionId,
    event,
    sportTypeId,
}) => {
    if (!eventDivisions?.length) {
        return {
            tabState: {},
            divisionIdToSetInQueryParam: null,
            divisionToFetchAcceptanceFor: null,
        };
    }

    const shouldShowAllDivisionsFilter = shouldDisplayAllDivisionsFilterInPlayerList(eventDivisions);
    const divisionToSet = shouldShowAllDivisionsFilter ? null : eventDivisions[0];
    const divisionIdToSetInQueryParam = divisionToSet ? divisionToSet.id : null;

    const tabState = {
        divisionId: divisionToSet?.id || '',
        divisionName: divisionToSet?.name || 'All divisions',
        registeredCount: event.registeredCount,
        selectedStatus: {
            value: '',
            label: 'All statuses',
        },
        selectedPosition: {
            value: '',
            label: 'All positions',
        },
        specificDivisionDateEnabled: null,
        specificDivisionDateUtc: null,
        sortParam: sportTypeId === sportTypeIds.PICKLEBALL ? sortParamTypes.SINGLESPKB : sortParamTypes.SINGLESUTR,
        sortType: SortType.DESC,
    };

    let divisionToFetchAcceptanceFor = null;
    const divisionIdToSetData = selectedDivisionId || divisionToSet?.id;
    const parsedId = divisionIdToSetData ? parseInt(divisionIdToSetData, 10) : NaN;
    const selectedDivision = !Number.isNaN(parsedId)
        ? eventDivisions.find(div => div.id === parsedId)
        : null;
    if (selectedDivision) {
        tabState.divisionId = selectedDivision.id;
        tabState.divisionName = selectedDivision.name;
        tabState.playerListDivisionFilters = [selectedDivision.id];
        tabState.divisionFormatTypeId = selectedDivision.divisionFormatTypeId;
        tabState.teamPaymentTypeId = selectedDivision.teamPaymentTypeId;
        tabState.registeredCount = selectedDivision.registeredCount;
        tabState.specificDivisionDateEnabled = selectedDivision.specificDivisionDateEnabled;
        tabState.specificDivisionDateUtc = selectedDivision.specificDivisionDateUtc;
        if (selectedDivision.isAcceptance) {
            divisionToFetchAcceptanceFor = selectedDivision.id;
        }
    }

    return { tabState, divisionIdToSetInQueryParam, divisionToFetchAcceptanceFor };
};

export const getWaitlistMoveConfig = (currentDivision, player, partner) => {
    const isDoubles = currentDivision?.divisionFormatTypeId !== divisionFormatTypeIds.SINGLES;
    const isMainFull = isDoubles
        ? (currentDivision?.maxTeams !== null && currentDivision?.nonAcceptanceMainCount >= currentDivision?.maxTeams)
        : (currentDivision?.maxPlayers !== null && currentDivision?.nonAcceptanceMainCount >= currentDivision?.maxPlayers);
    const playersList = isDoubles ? [player, partner].filter(Boolean) : [player];
    const playerNames = playersList.map(p => `${p.firstName} ${p.lastName}`).join(' and ');
    const memberIds = isDoubles ? [player.memberId, partner ? partner.memberId : null] : [player.memberId];
    return { isMainFull, playerNames, memberIds, isDoubles };
};
